<template>
  <van-dialog
    v-model="innerVisible"
    class="device-list"
    :title="title"
    show-cancel-button
    :beforeClose="beforeClose"
    @confirm="handleConfirm"
    @cancel="handleCancel"
  >
    <div class="main">
      <div class="btn-list">
        <van-button
          v-for="(item, index) in groups"
          :key="index"
          :name="index"
          :class="['terminal-btn', { selected: selectedIndex === index }]"
          icon="/static/images/etv-folder.png"
          @click="loadDeviceList(index, item.id)"
        >
          {{ truncatedName(item.name) }}
        </van-button>
      </div>

      <div class="btn-state">
        <van-radio-group v-model="onlineState" @change="onSearch" direction="horizontal">
          <span style="margin-right: 8px; display: flex; align-items: center">{{ $t('append_field.terminal_status') }}：</span>
          <van-radio name="0">{{ $t('append_field.all') }}</van-radio>
          <van-radio name="1">{{ $t('append_field.online') }}</van-radio>
          <van-radio name="-1">{{ $t('append_field.offline') }}</van-radio>
        </van-radio-group>
      </div>

      <div class="btn-select">
        <span>{{ $t('append_field.select_all') }}：</span>
        <van-checkbox v-model="isCheckAll" :indeterminate="isIndeterminate" @change="checkAllChange" />
      </div>

      <van-search
        v-model="clientName"
        :placeholder="$t('append_field.enter_search_keyword')"
        shape="round"
        @search="onSearch"
      />

      <div class="scroll-m-li" ref="scrollContainer">
        <van-checkbox-group v-model="terminalSelect" @change="checkedResultChange" v-if="data_m.length > 0">
          <van-list v-model="loading" :finished="finished" :finished-text="$t('append_field.noMore')" @load="onLoad">
            <div
              v-for="(item, index) in data_m"
              :key="index"
              @click="toggleCheckbox(item)"
              class="custom-checkbox"
            >
              <van-cell
                class="checkbox-content"
                :icon="item.onlineState == '1' ? '/static/images/terminal/icon_dev_online.png' : '/static/images/terminal/icon_dev_disonline.png'"
              >
                <span class="ellipsis">{{ item.name }}</span>
              </van-cell>
              <van-checkbox
                :class="['checkboxes-' + index, { 'custom-unchecked': !isChecked(item) }]"
                :name="item.clNo + '-' + item.clId"
                :checked="isChecked(item)"
              />
            </div>
          </van-list>
        </van-checkbox-group>

        <van-empty
          v-else
          class="custom-image"
          image="/static/images/terminal/empty-img.png"
          :description="$t('append_field.data_empty')"
        />
      </div>
    </div>
  </van-dialog>
</template>

<script>
export default {
  name: 'DeviceSelectDialog',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    beforeClose: {
      type: Function,
      default: null,
    },
    pageSize: {
      type: Number,
      default: 20,
    },
    fetchGroups: {
      type: Function,
      required: true,
    },
    fetchDevices: {
      type: Function,
      default: null,
    },
    initialOnlineState: {
      type: String,
      default: '0',
    },
    initialClientName: {
      type: String,
      default: '',
    },
    initialGroupParentId: {
      type: [String, Number],
      default: 1,
    },
  },
  data() {
    return {
      innerVisible: false,
      groups: [],
      selectedIndex: 0,
      groupParentId: 1,
      onlineState: '0',
      clientName: '',

      terminalSelect: [],
      isCheckAll: false,
      isIndeterminate: false,

      allDataList: [],
      data_m: [],
      pageNo: 1,
      loading: false,
      finished: false,
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(v) {
        this.innerVisible = v
        if (v) {
          this.init()
        }
      },
    },
    innerVisible(v) {
      this.$emit('input', v)
    },
  },
  methods: {
    async init() {
      this.onlineState = this.initialOnlineState
      this.clientName = this.initialClientName
      this.groupParentId = this.initialGroupParentId
      this.selectedIndex = 0
      this.resetSelection()

      await this.loadGroupsOnce(true)
      await this.reloadDevices()
    },

    resetSelection() {
      this.terminalSelect = []
      this.isCheckAll = false
      this.isIndeterminate = false
    },

    async loadGroupsOnce(resetGroupToFirst) {
      const groups = await this.fetchGroups({
        onlineState: this.onlineState,
        clientName: this.clientName,
        groupParentId: this.groupParentId,
      })
      this.groups = Array.isArray(groups) ? groups : []

      if (resetGroupToFirst) {
        this.selectedIndex = 0
        if (this.groups.length > 0 && this.groups[0] && this.groups[0].id != null) {
          this.groupParentId = this.groups[0].id
        }
      }
    },

    async reloadDevices() {
      this.pageNo = 1
      this.loading = true
      this.finished = false

      let list = []
      if (this.fetchDevices) {
        list = await this.fetchDevices({
          onlineState: this.onlineState,
          clientName: this.clientName,
          groupParentId: this.groupParentId,
        })
      } else {
        const group = this.groups[this.selectedIndex]
        list = group && Array.isArray(group.children) ? group.children : []
      }

      this.allDataList = Array.isArray(list) ? list : []
      this.data_m = this.allDataList.slice(0, this.pageSize)
      this.finished = this.data_m.length >= this.allDataList.length
      this.loading = false

      this.$nextTick(() => {
        const el = this.$refs.scrollContainer
        if (el) {
          el.scrollTop = 0
        }
      })
    },

    loadDeviceList(index, groupParentId) {
      this.selectedIndex = index
      this.groupParentId = groupParentId
      this.resetSelection()
      this.reloadDevices()
    },

    onSearch() {
      this.resetSelection()
      this.reloadDevices()
    },

    onLoad() {
      if (this.finished) {
        this.loading = false
        return
      }

      const nextPageNo = this.pageNo + 1
      const end = nextPageNo * this.pageSize
      this.data_m = this.allDataList.slice(0, end)
      this.pageNo = nextPageNo
      this.loading = false
      this.finished = this.data_m.length >= this.allDataList.length
    },

    checkAllChange() {
      if (this.isCheckAll) {
        this.terminalSelect = this.allDataList.map(item => `${item.clNo}-${item.clId}`)
      } else {
        this.terminalSelect = []
      }
      this.updateIndeterminateState()
    },

    checkedResultChange() {
      this.updateIndeterminateState()
    },

    updateIndeterminateState() {
      const total = this.allDataList.length
      const checkedCount = this.terminalSelect.length
      if (total === 0) {
        this.isCheckAll = false
        this.isIndeterminate = false
        return
      }
      this.isCheckAll = checkedCount === total
      this.isIndeterminate = checkedCount > 0 && checkedCount < total
    },

    toggleCheckbox(item) {
      const name = `${item.clNo}-${item.clId}`
      const index = this.terminalSelect.indexOf(name)
      if (index > -1) {
        this.terminalSelect.splice(index, 1)
      } else {
        this.terminalSelect.push(name)
      }
      this.updateIndeterminateState()
    },

    isChecked(item) {
      const name = `${item.clNo}-${item.clId}`
      return this.terminalSelect.includes(name)
    },

    truncatedName(fullName) {
      const namePart = fullName.split(' (')[0]
      const infoPart = fullName.includes('(') ? `${fullName.slice(fullName.indexOf('('))}` : ''
      if (namePart.length <= 4) {
        return fullName
      } else {
        return `${namePart.substring(0, 4)}...${infoPart}`
      }
    },

    handleConfirm() {
      if (this.terminalSelect.length === 0) {
        this.$emit('confirm-empty')
        return
      }
      const selectedDevices = this.allDataList.filter(item => this.terminalSelect.includes(`${item.clNo}-${item.clId}`))
      this.$emit('confirm', {
        terminalSelect: [...this.terminalSelect],
        selectedDevices,
        onlineState: this.onlineState,
        clientName: this.clientName,
        groupParentId: this.groupParentId,
      })
    },

    handleCancel() {
      this.resetSelection()
      this.$emit('cancel')
    },
  },
}
</script>
<style scoped lang="scss">
.device-list{
  display: flex;
  flex-direction: column;
  .main{
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  .btn-list{
    display: flex;
    overflow-y: auto;
    align-items: center;
    justify-content: flex-start;
    gap: 10px; /* 按钮之间的间隔 */
    margin:  10px 10px;

    .terminal-btn{
      flex: 1 1 auto; /* 自适应宽度 */
      white-space: nowrap; /* 防止文字换行 */
      background-color: white;
      color: black;
      border: 1px solid #ddd; /* 可选：添加边框以便更清晰地看到按钮 */
      .van-button__text{
        max-width: 100%;
      }
    }
    .selected {
      background-color: #006EFF ;
      color: white;
    }
  }
  .btn-state{
    padding: 5px 12px;
  }
  .btn-select{
    display: flex;
    align-items: center;
    padding: 5px 12px;
    span{
      padding-right: 10px;
    }
  }
  .scroll-m-li {
    height: 300px;
    display: flex;
    overflow-y: auto;
    margin: 0 20px;
    justify-content: space-around;
    .van-cell__value--alone{
      margin-left: 10px;
    }
    .van-checkbox{
      overflow: initial;
    }
    .van-icon{
      width: 1.5em;
      height: 1.5em;
    }
    .van-checkbox-group{
      width: 100%;
    }
    .custom-checkbox {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 0;
    }

    .checkbox-content {
      display: flex;
      align-items: center;
      .ellipsis {
        display: block;
        max-width: 200px; /* 根据需要调整宽度 */
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        vertical-align: top; /* 确保文本对齐 */
      }
      .van-cell__left-icon{
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .van-icon__image{
        width: 40px;
        height: 40px;
      }
    }

  }
}
</style>
